import type {
  AssistantConversationListRequest,
  AssistantConversationTranscriptResponse,
  AssistantFeedbackFilterRequest,
  AssistantFeedbackListResponse,
  AssistantMemoryResponse,
  AssistantToolStatsResponse,
  AssistantUsageOverviewResponse,
  AssistantUsageSeriesRowResponse,
  AssistantUserUsageResponse,
  FileResponse,
  GetListResponseOfAssistantConversationSummaryResponse,
} from 'src/api/ProactApiClient';
import {
  assistantControlCenterApiClient,
  globalAssistantControlCenterApiClient,
} from 'src/api/ProactApiClientInstances';
import {emptySplitApi} from '../api';
import {clientQuery, voidClientQuery} from '../clientQuery';

interface GlobalSeriesArgs {
  days: number;
  instanceId?: number;
}

// Query args carry ISO strings: a Date in originalArgs trips redux's serializable check.
interface UtcRangeArgs {
  fromUtc?: string;
  toUtc?: string;
}

export type AssistantConversationListArgs = Omit<
  AssistantConversationListRequest,
  'fromUtc' | 'toUtc'
> &
  UtcRangeArgs;

export type AssistantFeedbackFilterArgs = Omit<
  AssistantFeedbackFilterRequest,
  'fromUtc' | 'toUtc'
> &
  UtcRangeArgs;

export interface AssistantFeedbackListArgs extends AssistantFeedbackFilterArgs {
  start: number;
  length: number;
}

const toUtcRange = ({fromUtc, toUtc}: UtcRangeArgs) => ({
  fromUtc: fromUtc ? new Date(fromUtc) : undefined,
  toUtc: toUtc ? new Date(toUtc) : undefined,
});

const toRequest = <T extends UtcRangeArgs>(args: T) => ({
  ...args,
  ...toUtcRange(args),
});

export interface AssistantUserMemoryTarget {
  userId: number;
  id: number;
}

const userMemoryTag = (userId: number) =>
  ({type: 'AssistantMemory', id: `user-${userId}`}) as const;

export const assistantControlCenterApi = emptySplitApi.injectEndpoints({
  endpoints: builder => ({
    // Instance-scoped control center
    assistantOverview: builder.query<AssistantUsageOverviewResponse, void>({
      queryFn: clientQuery(() => assistantControlCenterApiClient.overview()),
    }),
    assistantUsageSeries: builder.query<AssistantUsageSeriesRowResponse[], number>({
      queryFn: clientQuery(days =>
        assistantControlCenterApiClient.usageSeries(days)
      ),
    }),
    assistantUserUsage: builder.query<AssistantUserUsageResponse[], number>({
      queryFn: clientQuery(days => assistantControlCenterApiClient.users(days)),
    }),
    assistantToolStats: builder.query<AssistantToolStatsResponse, number>({
      queryFn: clientQuery(days =>
        assistantControlCenterApiClient.toolStats(days, undefined)
      ),
    }),
    assistantUserMemory: builder.query<AssistantMemoryResponse[], number>({
      queryFn: clientQuery(userId =>
        assistantControlCenterApiClient.userMemory(userId)
      ),
      providesTags: (_result, _error, userId) => [userMemoryTag(userId)],
    }),
    deleteAssistantUserMemory: builder.mutation<void, AssistantUserMemoryTarget>({
      queryFn: voidClientQuery(({userId, id}) =>
        assistantControlCenterApiClient.deleteUserMemory(userId, id)
      ),
      invalidatesTags: (_result, _error, {userId}) => [userMemoryTag(userId)],
    }),
    assistantConversations: builder.query<
      GetListResponseOfAssistantConversationSummaryResponse,
      AssistantConversationListArgs
    >({
      queryFn: clientQuery(args =>
        assistantControlCenterApiClient.listConversations(toRequest(args))
      ),
    }),
    assistantTranscript: builder.query<
      AssistantConversationTranscriptResponse,
      number
    >({
      queryFn: clientQuery(id => assistantControlCenterApiClient.transcript(id)),
    }),
    assistantFeedback: builder.query<
      AssistantFeedbackListResponse,
      AssistantFeedbackListArgs
    >({
      queryFn: clientQuery(args =>
        assistantControlCenterApiClient.listFeedback(toRequest(args))
      ),
    }),
    exportAssistantFeedback: builder.mutation<
      FileResponse | null,
      AssistantFeedbackFilterArgs
    >({
      queryFn: clientQuery(args =>
        assistantControlCenterApiClient.exportFeedback(toRequest(args))
      ),
    }),

    // Platform-wide (super admin) control center
    globalAssistantOverview: builder.query<AssistantUsageOverviewResponse, void>({
      queryFn: clientQuery(() =>
        globalAssistantControlCenterApiClient.overview()
      ),
    }),
    globalAssistantUsageSeries: builder.query<
      AssistantUsageSeriesRowResponse[],
      GlobalSeriesArgs
    >({
      queryFn: clientQuery(({days, instanceId}) =>
        globalAssistantControlCenterApiClient.usageSeries(days, instanceId)
      ),
    }),
    globalAssistantUserUsage: builder.query<
      AssistantUserUsageResponse[],
      GlobalSeriesArgs
    >({
      queryFn: clientQuery(({days, instanceId}) =>
        globalAssistantControlCenterApiClient.users(days, instanceId)
      ),
    }),
    globalAssistantConversations: builder.query<
      GetListResponseOfAssistantConversationSummaryResponse,
      AssistantConversationListArgs
    >({
      queryFn: clientQuery(args =>
        globalAssistantControlCenterApiClient.listConversations(toRequest(args))
      ),
    }),
    globalAssistantTranscript: builder.query<
      AssistantConversationTranscriptResponse,
      number
    >({
      queryFn: clientQuery(id =>
        globalAssistantControlCenterApiClient.transcript(id)
      ),
    }),
    globalAssistantFeedback: builder.query<
      AssistantFeedbackListResponse,
      AssistantFeedbackListArgs
    >({
      queryFn: clientQuery(args =>
        globalAssistantControlCenterApiClient.listFeedback(toRequest(args))
      ),
    }),
    exportGlobalAssistantFeedback: builder.mutation<
      FileResponse | null,
      AssistantFeedbackFilterArgs
    >({
      queryFn: clientQuery(args =>
        globalAssistantControlCenterApiClient.exportFeedback(toRequest(args))
      ),
    }),
  }),
});

export const {
  useAssistantOverviewQuery,
  useAssistantUsageSeriesQuery,
  useAssistantUserUsageQuery,
  useAssistantToolStatsQuery,
  useAssistantUserMemoryQuery,
  useDeleteAssistantUserMemoryMutation,
  useAssistantConversationsQuery,
  useAssistantTranscriptQuery,
  useAssistantFeedbackQuery,
  useExportAssistantFeedbackMutation,
  useGlobalAssistantOverviewQuery,
  useGlobalAssistantUsageSeriesQuery,
  useGlobalAssistantUserUsageQuery,
  useGlobalAssistantConversationsQuery,
  useGlobalAssistantTranscriptQuery,
  useGlobalAssistantFeedbackQuery,
  useExportGlobalAssistantFeedbackMutation,
} = assistantControlCenterApi;
